import {
  ALLOWED_MEDIA,
  preloadResponsiveImage,
  type ResponsiveImageSource,
} from '@/app/components/media/app-image';
import type { SpartanProductAdasData } from '@/app/lcv/components/sections/spartan/products/adas/types';
import {
  SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_CHANGE_EVENT,
  SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_QUERY,
} from '@/app/lcv/components/sections/spartan/products/explore-features/constants';
import type {
  SpartanProductExploreFeaturesDetailData,
  SpartanProductExploreFeaturesDetailsMap,
} from '@/app/lcv/components/sections/spartan/products/explore-features/types';
import { isBrowser } from '@/app/utils/browser';

const EXPLORE_DETAIL_HISTORY_STATE_KEY = '__spartanExploreDetail';

type ExploreCoverFetchPriority = 'high' | 'low';

const getHistoryState = (): Record<string, unknown> => {
  const state = window.history.state;
  return state && typeof state === 'object'
    ? (state as Record<string, unknown>)
    : {};
};

export const getSpartanExploreFeaturesDetailId = (
  search = isBrowser ? window.location.search : ''
) =>
  new URLSearchParams(search).get(
    SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_QUERY
  );

export const getSpartanExploreCoverSources = (
  detail?: SpartanProductExploreFeaturesDetailData
): ResponsiveImageSource[] => {
  const mobile = detail?.imageCover?.mobile.src;
  const desktop = detail?.imageCover?.desktop.src;
  if (!mobile && !desktop) return [];

  return [
    {
      src: mobile || desktop || '',
      sizes: '100vw',
      widths: [640, 750, 828, 1080, 1280, 1440, 1680, 1920, 2048],
      media: ALLOWED_MEDIA.lgDown,
      params: { ar: '16-9' },
    },
    {
      src: desktop || mobile || '',
      sizes: '(min-width: 1280px) 1232px, 100vw',
      widths: [1280, 1440, 1680, 1920, 2048, 2560, 2880],
      media: ALLOWED_MEDIA.lgUp,
      params: { ar: '8-3' },
    },
  ];
};

export const preloadSpartanExploreCover = (
  details: SpartanProductExploreFeaturesDetailsMap | undefined,
  detailId: string,
  fetchPriority: ExploreCoverFetchPriority
) => {
  getSpartanExploreCoverSources(details?.[detailId]).forEach((source) => {
    preloadResponsiveImage(source, fetchPriority);
  });
};

export const setSpartanExploreFeaturesDetailId = (
  pathname: string,
  detailId: string | null
) => {
  if (!isBrowser) return;

  const url = new URL(window.location.href);
  url.pathname = pathname;

  if (detailId) {
    url.searchParams.set(
      SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_QUERY,
      detailId
    );
  } else {
    url.searchParams.delete(SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_QUERY);
  }

  const relativeUrl = `${url.pathname}${url.search}${url.hash}`;
  const historyState = getHistoryState();
  const ownsHistoryEntry =
    historyState[EXPLORE_DETAIL_HISTORY_STATE_KEY] === true;

  if (detailId) {
    const nextState = {
      ...historyState,
      [EXPLORE_DETAIL_HISTORY_STATE_KEY]: true,
    };

    if (ownsHistoryEntry) {
      window.history.replaceState(nextState, '', relativeUrl);
    } else {
      window.history.pushState(nextState, '', relativeUrl);
    }
  } else if (ownsHistoryEntry) {
    window.history.back();
    return;
  } else {
    window.history.replaceState(historyState, '', relativeUrl);
  }

  window.dispatchEvent(
    new Event(SPARTAN_PRODUCT_EXPLORE_FEATURES_DETAIL_CHANGE_EVENT)
  );
};

export const mapSpartanAdasToExploreDetail = (
  adas: SpartanProductAdasData,
  imageCover: { mobile: string; desktop: string }
): SpartanProductExploreFeaturesDetailData => ({
  imageCover: {
    mobile: { src: imageCover.mobile },
    desktop: { src: imageCover.desktop },
  },
  items: adas.features.map((feature) => ({
    images: [{ src: feature.image.src }],
    video: feature.video
      ? {
          src: feature.video.src,
          poster: feature.image.src,
          theme: feature.video.theme,
        }
      : undefined,
    features: [
      {
        eyebrow: feature.eyebrow,
        title: feature.title,
        subTitle: feature.subTitle,
        description: feature.description,
      },
    ],
  })),
});
